import { Injectable } from '@angular/core';
import { ENDPOINTS } from '../../enums/endpoints.enum';
import { isDefined } from '../../utilities/is-defined';
import { AppNotificationType } from '../app-notification/app-notification.model';
import { AppNotificationService } from '../app-notification/app-notification.service';
import {
    APPOINTMENT_SLOT_STATUS,
    AppointmentSlotsModel,
    ODataGetAppointmentPayload,
    OdataAppointmentBookPayload,
    OdataAppointmentBookResponsePayload,
    OdataAppointmentSlotResponseModel,
} from './appointment-booking-model';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, map, mergeMap, take, tap } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { EnvironmentService } from '@evorait/pace-environment';
import { TranslateService } from '@ngx-translate/core';
export enum AppBookingConfirmationCodes {
    SUCCESS = 1,
    ERROR = 2,
}
@Injectable()
export class AppointmentBookingService {
    public constructor(
        private readonly httpClient: HttpClient,
        private readonly environmentService: EnvironmentService,
        private readonly appNotificationService: AppNotificationService,
        private readonly translate: TranslateService,
    ) {}

    public getAppointment(model: AppointmentSlotsModel): Observable<OdataAppointmentSlotResponseModel> {
        const payload = model.getAppointmentGetPayload();
        return this.getAppointmentFromBackend(payload).pipe(
            take(1),
            tap((response) => model.updateAppointmentSlots(response)),
            catchError(() => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('CONFIRMATION_FOLLOWUP_SEARCH_FOR_APPOINTMENTS_FAILED_TITLE'),
                    <string>this.translate.instant('CONFIRMATION_FOLLOWUP_SEARCH_FOR_APPOINTMENTS_FAILED_MESSAGE'),
                );
                return EMPTY;
            }),
        );
    }

    private getAppointmentFromBackend(
        payload: ODataGetAppointmentPayload,
    ): Observable<OdataAppointmentSlotResponseModel> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.AppointmentGet}`;
        return this.httpClient.post<OdataAppointmentSlotResponseModel>(url, payload).pipe(
            map((response: OdataAppointmentSlotResponseModel) => {
                if (!response) {
                    throw new Error(
                        `AppointmentBookingService: getAppointmentFromBackend: Could not get appointment slots!`,
                    );
                }
                return response;
            }),
        );
    }

    public bookAppointment(model: AppointmentSlotsModel, index: number): Observable<boolean> {
        const payload = model.getAppointmentBookPayloadForIndex(index);
        return this.bookAppointmentFromBackend(payload).pipe(
            mergeMap((status) => {
                model.updateLoadingStatus(index, false);

                if (!isDefined(status)) {
                    return of(false);
                }

                model.updatedAppointmentStatus(index, status);
                const isSlotAvailable = model.slotIntervals.value[index].status === APPOINTMENT_SLOT_STATUS.AVAILABLE;
                model.isAppointmentBooked = isSlotAvailable;

                return of(isSlotAvailable);
            }),
            catchError(() => {
                this.appNotificationService.setAppNotification(
                    AppNotificationType.ERROR,
                    <string>this.translate.instant('CONFIRMATION_FOLLOWUP_BOOK_APPOINTMENTS_FAILED_TITLE'),
                    <string>this.translate.instant('CONFIRMATION_FOLLOWUP_BOOK_APPOINTMENTS_FAILED_MESSAGE'),
                );
                return of(false);
            }),
        );
    }

    private bookAppointmentFromBackend(payload: OdataAppointmentBookPayload): Observable<boolean> {
        const url = `${this.environmentService.apiBase}${ENDPOINTS.AppointmentBook}`;
        return this.httpClient.post(url, payload).pipe(
            map((response: OdataAppointmentBookResponsePayload) => {
                if (!response) {
                    throw new Error(
                        `AppointmentBookingService: bookAppointmentFromBackend: Could not book the selected appointment slot!`,
                    );
                }
                return response.StatusCode === AppBookingConfirmationCodes.SUCCESS;
            }),
        );
    }
}
